// Copyright (C) Okahu Inc 2023-2024. All rights reserved.

// import { fetchKPI } from '@/services/kpiServices';
import Image from 'next/image';
import { useLanguage } from '@/providers/LanguageProvider';
import { formatApiDate } from '@/utils/formatedDates';
import { getAlertFactConfig } from '@/utils/getAlertFactConfig';
import { getTenantTimeRange } from '@/utils/getTenantTimeRange';
import Active from 'public/shared-icons/Active.svg';
import { AppListItem } from 'types/app-list';

import { Separator } from '@/components/ui/separator';

import { Icons, System } from '../icons';
import {
  Tooltip,
  TooltipArrow,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../ui/tooltip';
import { AppDropdownMenu } from './app-dropdown-menu';

interface AppCardProps {
  app: AppListItem;
  onPinToggle: (appName: string) => void;
  isPinned: boolean;
  loading: boolean;
}

export const AppCards: React.FC<AppCardProps> = ({
  app,
  onPinToggle,
  isPinned,
  loading,
}) => {
  const handlePinToggle = () => {
    onPinToggle(app.name);
  };

  const { messages } = useLanguage();

  const performanceAlert = getAlertFactConfig(app.sla_config, 'performance');
  const budgetAlert = getAlertFactConfig(app.sla_config, 'budget');

  const hasCategories =
    Array.isArray(app.categories) && app.categories.length > 0;

  return (
    <div className="group flex h-full flex-col justify-between rounded-lg border border-line-secondary bg-canvas-background-primary p-4 shadow-[0px_0px_8px_0px_#00000040] hover:border-form-control-border-focus hover:bg-card-02 hover:shadow-[2px_8px_16px_0px_rgba(0,0,0,0.32)]">
      {/* TODO: shadow tailwind config */}
      <div className="flex w-full items-center justify-between">
        {hasCategories ? (
          <div className="flex items-center gap-1">
            {app.categories.slice(0, 2).map((category, index) => (
              <span
                key={index}
                className=" cursor-default rounded-3xl bg-white/[.08] px-2 pb-[1px] pt-[1.5px] text-body-sm text-body-subtle"
              >
                {category.length > 16
                  ? `${category.slice(0, 16)}...`
                  : category}
              </span>
            ))}
            {app.categories.length > 2 && (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span className=" cursor-pointer rounded-3xl bg-white/[.08] px-2 pb-[1px] pt-[1.5px] text-body-sm text-body-subtle">
                      + {app.categories.length - 2}
                    </span>
                  </TooltipTrigger>
                  <TooltipContent
                    side="top"
                    align="start"
                    className="max-w-52 rounded"
                  >
                    <ul className="list-inside space-y-1">
                      {app.categories.slice(2).map((category, index) => (
                        <li key={index}>{category}</li>
                      ))}
                    </ul>
                    <TooltipArrow className="fill-fill-form-control-selected" />
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
          </div>
        ) : (
          <h3
            data-testid="app-card-title"
            className="text-h4 font-medium text-body-primary"
          >
            {app.display_name}
          </h3>
        )}
        <div className="ml-auto flex items-center gap-2">
          {isPinned && (
            <div className="brightness-0 invert">{Icons.Pin('size-4')}</div>
          )}
          <AppDropdownMenu
            isPinned={isPinned}
            onPinToggle={handlePinToggle}
            appName={app.name}
          />
        </div>
      </div>
      <div className="mb-2 h-[55px]">
        {hasCategories && (
          <h3
            data-testid="app-card-title"
            className="mt-1 text-h4 font-medium text-body-primary"
          >
            {app.display_name}
          </h3>
        )}
        <p className="mt-1 line-clamp-2 text-body-sm text-body-subtle">
          {app.description}
        </p>
      </div>
      {!loading
        ? (app.errors > 0 ||
            app.not_meet_sla > 0 ||
            app.not_meet_budget_sla > 0 ||
            app.test_failures > 0) && (
            <div className="grid grid-cols-2 gap-2 pb-2 pt-3 text-form-input-text text-body-subtle">
              {app.errors > 0 && (
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <div className="flex items-center gap-1">
                        <span>
                          {System.InfoIcon('h-[16px] w-[16px] text-red-500')}
                        </span>
                        <span>{messages?.General?.error}:</span>
                        <span>{app.errors}</span>
                      </div>
                    </TooltipTrigger>
                    <TooltipContent
                      side="top"
                      align="center"
                      className="max-w-52 rounded"
                    >
                      {messages?.Measures.app_details_error}
                      {''} {''}
                      {getTenantTimeRange() ||
                        messages?.PromptFilters?.date_range?.last7}
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              )}
              {app.not_meet_sla > 0 && (
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <div className="flex items-center gap-1">
                        <span>
                          {System.BarIcon('h-[16px] w-[16px] text-[#FC8A13]')}
                        </span>
                        <span>{messages?.General?.performance}:</span>
                        <span>{app.not_meet_sla}</span>
                      </div>
                    </TooltipTrigger>
                    <TooltipContent
                      side="top"
                      align="center"
                      className="max-w-52 rounded"
                    >
                      {`Count of ${performanceAlert?.label} with a GenAI operation that did not meet the configured SLA of ${performanceAlert?.value} ${performanceAlert?.unit} in the ${getTenantTimeRange() || messages?.PromptFilters?.date_range?.last7}`}
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              )}
              {app.not_meet_budget_sla > 0 && (
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <div className="flex items-center gap-1">
                        <span>
                          {System.BudgetIcon(
                            'h-[16px] w-[16px] text-[#DFC113]'
                          )}
                        </span>
                        <span>{messages?.General?.budget}:</span>
                        <span>{app.not_meet_budget_sla}</span>
                      </div>
                    </TooltipTrigger>
                    <TooltipContent
                      side="top"
                      align="center"
                      className="max-w-52 rounded"
                    >
                      {`Count of ${budgetAlert?.label} with a GenAI operation that exceeded the configured budget of ${budgetAlert?.value} ${budgetAlert?.unit} in the ${getTenantTimeRange() || messages?.PromptFilters?.date_range?.last7}`}
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              )}
              {app.test_failures > 0 && (
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <div className="flex items-center gap-1">
                        <span>
                          {System.TestFailuresIcon(
                            'h-[16px] w-[16px] text-[#DFC113]'
                          )}
                        </span>
                        <span>{messages?.General?.test_failures}:</span>
                        <span>{app.test_failures}</span>
                      </div>
                    </TooltipTrigger>
                    <TooltipContent
                      side="top"
                      align="center"
                      className="max-w-52 rounded"
                    >
                      {messages?.Measures.app_details_test_failures} {''}
                      {getTenantTimeRange() ||
                        messages?.PromptFilters?.date_range?.last7}
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              )}
            </div>
          )
        : null}
      <Separator className="my-2 bg-line-tertiary" />
      <div className="flex w-full items-center justify-between gap-5 text-[#8C9EA1]">
        <div className="flex items-center gap-1">
          <Image src={Active} alt="Active" />
          <span className="text-body capitalize text-body-subtle">
            {app.status}
          </span>
        </div>
        <span className="text-body-sm text-form-input-disabled">
          {messages?.AppCards?.updated}: {formatApiDate(app.last_updated_at)}
        </span>
      </div>
      {/* <div className="mt-[13px] flex items-center justify-between">
        <div className="flex flex-col items-center">
          <p className="text-h4 font-semibold text-body-subtle">
            {kpiValues[app.name].total_request_count}
          </p>
          <p className="text-[10px] font-normal leading-[18px] text-white">
            traces
          </p>
        </div>
        <div className="flex flex-col items-center">
          <p className="text-h4 font-semibold text-body-subtle">3.8s</p>
          <p className="text-[10px] font-normal leading-[18px] text-white">
            avg. duration
          </p>
        </div>
        <div className="flex flex-col items-center">
          <p className="text-h4 font-semibold text-body-subtle">active</p>
          <p className="text-[10px] font-normal leading-[18px] text-white">
            status
          </p>
        </div>
      </div> */}
    </div>
  );
};
